import dayjs, { Dayjs } from "dayjs";
import {
  DemandAdjustment,
  DemandAdjustmentTargets,
  DemandAdjustmentType,
  DemandAdjustmentWriteRequest,
} from "../../types/demand-factor.type";
import {
  API_DATE_FORMAT,
  EMPTY_TARGETS,
  MAX_MULTIPLIER,
  MIN_MULTIPLIER,
} from "./constants";

export interface DemandFactorFormValues {
  name: string;
  /** "" until one is picked. */
  type: DemandAdjustmentType | "";
  /** As typed, so a half-typed "1." survives the round trip through the input. */
  multiplier: string;
  validFrom: Dayjs | null;
  validTo: Dayjs | null;
  targets: DemandAdjustmentTargets;
}

export interface DemandFactorFormErrors {
  name?: string;
  type?: string;
  multiplier?: string;
  validFrom?: string;
  validTo?: string;
}

const MULTIPLIER_RANGE_ERROR = `ตัวคูณต้องอยู่ระหว่าง ${MIN_MULTIPLIER} ถึง ${MAX_MULTIPLIER}`;

/** A bare `YYYY-MM-DD` is read as that calendar day, with no time-zone shift. */
const parseBusinessDate = (date: string | null): Dayjs | null => (date ? dayjs(date) : null);

/**
 * The form a dialog opens on: the row being edited, or an empty one. A branch manager's new
 * row starts on its own branch, which the server enforces anyway.
 */
export const initialFormValues = (
  row: DemandAdjustment | null,
  ownBranchCode: string | null,
): DemandFactorFormValues => {
  if (!row) {
    return {
      name: "",
      type: "",
      multiplier: "",
      validFrom: null,
      validTo: null,
      targets: {
        ...EMPTY_TARGETS,
        target_branch: ownBranchCode ? [ownBranchCode] : [],
      },
    };
  }
  return {
    name: row.name ?? "",
    type: row.type,
    multiplier: row.multiplier === null ? "" : String(row.multiplier),
    validFrom: parseBusinessDate(row.valid_from),
    validTo: parseBusinessDate(row.valid_to),
    targets: {
      target_branch: row.target_branch,
      target_route: row.target_route,
      target_customer_group: row.target_customer_group,
      target_channel: row.target_channel,
      exclude_channel: row.exclude_channel,
      target_product_code: row.target_product_code,
    },
  };
};

/** The typed multiplier as a number inside the API's bounds, or null. */
export const parseMultiplier = (text: string): number | null => {
  if (text.trim() === "") return null;
  const multiplier = Number(text);
  const isInRange =
    Number.isFinite(multiplier) &&
    multiplier >= MIN_MULTIPLIER &&
    multiplier <= MAX_MULTIPLIER;
  return isInRange ? multiplier : null;
};

/**
 * Field errors. A value that is wrong as typed (out of range, dates in the wrong order)
 * shows at once; a field left empty is only flagged once `submitted` is set.
 */
export const validateForm = (
  values: DemandFactorFormValues,
  submitted: boolean,
): DemandFactorFormErrors => {
  const errors: DemandFactorFormErrors = {};
  const multiplierTyped = values.multiplier.trim() !== "";
  const { validFrom, validTo } = values;

  if (multiplierTyped && parseMultiplier(values.multiplier) === null) {
    errors.multiplier = MULTIPLIER_RANGE_ERROR;
  }
  if (validFrom?.isValid() && validTo?.isValid() && validTo.isBefore(validFrom, "day")) {
    errors.validTo = "วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่ม";
  }
  if (!submitted) return errors;

  if (!values.name.trim()) errors.name = "กรุณากรอกชื่อ";
  if (!values.type) errors.type = "กรุณาเลือกประเภท";
  if (!multiplierTyped) errors.multiplier = "กรุณากรอกตัวคูณ";
  if (!validFrom?.isValid()) errors.validFrom = "กรุณาเลือกวันที่เริ่ม";
  if (!validTo?.isValid()) errors.validTo = "กรุณาเลือกวันที่สิ้นสุด";
  return errors;
};

export const hasErrors = (errors: DemandFactorFormErrors): boolean =>
  Object.values(errors).some(Boolean);

/** The request body, or null while a required value is still missing or invalid. */
export const toWriteRequest = (
  values: DemandFactorFormValues,
): DemandAdjustmentWriteRequest | null => {
  const multiplier = parseMultiplier(values.multiplier);
  const { type, validFrom, validTo } = values;
  const name = values.name.trim();
  if (!name || !type || multiplier === null || !validFrom?.isValid() || !validTo?.isValid()) {
    return null;
  }
  return {
    name,
    type,
    multiplier,
    valid_from: validFrom.format(API_DATE_FORMAT),
    valid_to: validTo.format(API_DATE_FORMAT),
    ...values.targets,
  };
};
